import { useState, useCallback, useRef, useEffect } from 'react';
import { loadGoogleMaps } from '@/lib/google-maps';
import type { PlaceData } from '@/types/location';

interface UsePlacesOptions {
  language?: 'en' | 'lv';
  country?: string; // ISO 3166-1 Alpha-2 country code (e.g., 'lv' for Latvia)
  city?: string; // City name for location bias
}

export function usePlaces(options: UsePlacesOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  // In-memory cache for place_id → PlaceData within current picker session
  const placeCacheRef = useRef<Map<string, PlaceData>>(new Map());

  /**
   * Load Google Maps/Places libraries
   */
  const loadPlacesLibraries = useCallback(async (language?: string): Promise<boolean> => {
    console.log('[usePlaces] ========== loadPlacesLibraries() called ==========');
    console.log('[usePlaces] Parameters:', {
      language: language || options.language,
      optionsLanguage: options.language,
      hasOptionsCountry: !!options.country,
      hasOptionsCity: !!options.city,
    });

    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[usePlaces] Calling loadGoogleMaps...');
      const startTime = Date.now();
      await loadGoogleMaps({ language: language || options.language });
      const duration = Date.now() - startTime;
      console.log('[usePlaces] ✅ loadGoogleMaps completed successfully in', duration, 'ms');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load Google Maps';
      console.error('[usePlaces] ❌ loadPlacesLibraries failed:', {
        error: errorMessage,
        errorType: err instanceof Error ? err.constructor.name : typeof err,
        errorStack: err instanceof Error ? err.stack : undefined,
        hasError: !!err,
      });
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
      console.log('[usePlaces] loadPlacesLibraries finished, isLoading set to false');
    }
  }, [options.language, options.country, options.city]);

  /**
   * Create a new session token for autocomplete
   */
  const createSessionToken = useCallback((): google.maps.places.AutocompleteSessionToken => {
    if (!window.google?.maps?.places) {
      throw new Error('Google Places library not loaded');
    }
    return new window.google.maps.places.AutocompleteSessionToken();
  }, []);

  /**
   * Create autocomplete instance with session token
   */
  const createAutocompleteSession = useCallback((
    input: HTMLInputElement,
    sessionToken: google.maps.places.AutocompleteSessionToken,
    restrictions?: { country?: string; cityBounds?: google.maps.LatLngBounds; strictBounds?: boolean; location?: google.maps.LatLng; radius?: number }
  ): google.maps.places.Autocomplete => {
    if (!window.google?.maps?.places) {
      throw new Error('Google Places library not loaded');
    }

    const autocompleteOptions: google.maps.places.AutocompleteOptions = {
      // Consider car_repair type, but keep establishment as fallback for broader results
      types: ['establishment'], // Use only establishment (repair shops fall under this)
      fields: ['place_id', 'name', 'formatted_address', 'geometry', 'international_phone_number', 'website'],
      sessionToken,
    };

    // Add location bias if provided (for location-specific results)
    if (restrictions?.location) {
      autocompleteOptions.location = restrictions.location;
      if (restrictions.radius) {
        autocompleteOptions.radius = restrictions.radius;
      }
    }

    // Add country restriction (always applied when country provided)
    // Validate country code is a valid ISO 3166-1 Alpha-2 code (2 letters)
    if (restrictions?.country) {
      const countryCode = Array.isArray(restrictions.country) 
        ? restrictions.country[0]?.toLowerCase()
        : restrictions.country.toLowerCase();
      
      // Only use if it's a valid 2-letter country code (skip "unknown" and invalid codes)
      if (countryCode && countryCode !== 'unknown' && /^[a-z]{2}$/.test(countryCode)) {
        autocompleteOptions.componentRestrictions = {
          country: Array.isArray(restrictions.country) ? restrictions.country.map(c => c.toLowerCase()).filter(c => c !== 'unknown' && /^[a-z]{2}$/.test(c)) : [countryCode],
        };
        console.log('[DEBUG] Setting autocomplete country restriction to:', countryCode);
      } else {
        // Silently skip invalid country codes (don't log warnings for "unknown" - it's expected)
        if (countryCode !== 'unknown') {
          console.warn('[DEBUG] Invalid country code provided, skipping restriction:', restrictions.country);
        }
      }
    }

    const autocomplete = new window.google.maps.places.Autocomplete(input, autocompleteOptions);

    // Set city bounds if provided (for strict city filtering)
    if (restrictions?.cityBounds) {
      autocomplete.setBounds(restrictions.cityBounds);
      if (restrictions.strictBounds) {
        autocomplete.setOptions({ strictBounds: true });
      }
    }

    autocompleteRef.current = autocomplete;
    sessionTokenRef.current = sessionToken;

    return autocomplete;
  }, []);

  /**
   * Get place details by place_id with caching
   */
  const getPlaceDetails = useCallback(async (
    placeId: string,
    sessionToken?: google.maps.places.AutocompleteSessionToken
  ): Promise<PlaceData | null> => {
    // Check cache first
    const cached = placeCacheRef.current.get(placeId);
    if (cached) {
      return cached;
    }

    if (!window.google?.maps?.places) {
      throw new Error('Google Places library not loaded');
    }

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));

    return new Promise((resolve, reject) => {
      const request: google.maps.places.PlaceDetailsRequest = {
        placeId: placeId,
        // Fields needed for place info card and form autofill
        fields: ['place_id', 'name', 'formatted_address', 'geometry', 'formatted_phone_number', 'international_phone_number', 'website'],
      };

      // Add session token if provided (for billing optimization)
      if (sessionToken) {
        request.sessionToken = sessionToken;
      }

      service.getDetails(request, (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          reject(new Error(`Place details request failed: ${status}`));
          return;
        }

        // Normalize phones: parse, dedupe, return as array
        const normalizePhones = (formatted?: string | null, international?: string | null): string[] => {
          const set = new Set<string>();
          const maybe = [
            ...(formatted?.split(',') ?? []),
            ...(international?.split(',') ?? []),
          ].map(s => s.trim()).filter(Boolean);
          maybe.forEach(p => set.add(p));
          return Array.from(set);
        };

        const phones = normalizePhones(
          place.formatted_phone_number,
          place.international_phone_number
        );

        const placeData: PlaceData = {
          place_id: place.place_id || null,
          name: place.name || null,
          formatted_address: place.formatted_address || null,
          phones,
          website: place.website || null,
          lat: place.geometry?.location?.lat() || 0,
          lng: place.geometry?.location?.lng() || 0,
        };

        // Cache the result
        placeCacheRef.current.set(placeId, placeData);
        resolve(placeData);
      });
    });
  }, []);

  /**
   * Reverse geocode coordinates to get address
   */
  const reverseGeocode = useCallback(async (
    lat: number,
    lng: number,
    language?: string
  ): Promise<PlaceData | null> => {
    if (!window.google?.maps) {
      throw new Error('Google Maps library not loaded');
    }

    const geocoder = new window.google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode(
        {
          location: { lat, lng },
          language: language || options.language || 'en',
        },
        (results, status) => {
          if (status !== window.google.maps.GeocoderStatus.OK || !results || results.length === 0) {
            reject(new Error(`Reverse geocode failed: ${status}`));
            return;
          }

          const result = results[0];
          const placeData: PlaceData = {
            formatted_address: result.formatted_address || null,
            name: result.name || null,
            phones: [], // Reverse geocode doesn't provide phone numbers
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };

          // Try to extract place_id if available
          if (result.place_id) {
            placeData.place_id = result.place_id;
          }

          resolve(placeData);
        }
      );
    });
  }, [options.language]);

  /**
   * Get city viewport bounds for autocomplete restrictions
   */
  const getCityBounds = useCallback(async (
    cityName: string,
    country?: string
  ): Promise<google.maps.LatLngBounds | null> => {
    if (!window.google?.maps) {
      return null; // Silently fail if Maps not loaded
    }

    // Wait for Geocoder to be available (it's part of core Maps API)
    let retries = 0;
    while (!window.google.maps.Geocoder && retries < 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }

    if (!window.google.maps.Geocoder) {
      console.warn('[usePlaces] Geocoder not available, skipping city bounds');
      return null;
    }

    const geocoder = new window.google.maps.Geocoder();
    const address = country ? `${cityName}, ${country.toUpperCase()}` : cityName;

    return new Promise((resolve, reject) => {
      geocoder.geocode(
        { address, language: options.language || 'en' },
        (results, status) => {
          if (status !== window.google.maps.GeocoderStatus.OK || !results || results.length === 0) {
            resolve(null); // Return null instead of rejecting - allows graceful fallback
            return;
          }

          const viewport = results[0].geometry.viewport;
          if (viewport) {
            resolve(viewport);
          } else {
            resolve(null);
          }
        }
      );
    });
  }, [options.language]);

  /**
   * Clear current session token and cache
   */
  const clearSession = useCallback(() => {
    sessionTokenRef.current = null;
    autocompleteRef.current = null;
    placeCacheRef.current.clear();
  }, []);

  return {
    isLoading,
    error,
    loadPlacesLibraries,
    createSessionToken,
    createAutocompleteSession,
    getPlaceDetails,
    reverseGeocode,
    getCityBounds,
    clearSession,
  };
}

